"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { ArrowRight, BriefcaseBusiness, Code2, Sparkles } from "lucide-react";
import { useTranslations } from "next-intl";
import { useAuth } from "@/components/providers/auth-provider";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { Skeleton } from "@/components/ui/skeleton";
import { formatNumber } from "@/lib/format-datetime";
import { useSignInHref } from "@/lib/use-sign-in-href";
import { cn } from "@/lib/utils";
import { JudgeDarkHead } from "../components/shell/JudgeDarkHead";
import { DifficultyTag } from "../components/DifficultyTag";
import { SolvedMark } from "../components/SolvedMark";
import { SolveCount, solveCountLabels, type SolveCountLabels } from "../components/SolveCount";
import { useInterviewPrep } from "./api";
import {
  INTERVIEW_TOPICS,
  isInterviewTopic,
  type InterviewPrepList,
  type InterviewPrepProblem,
  type InterviewTopic,
} from "./types";

/** The message key of a topic's name (`judge.interview.topic_*`). */
const TOPIC_KEY: Readonly<Record<InterviewTopic, string>> = {
  array: "topic_array",
  string: "topic_string",
  "hash-map": "topic_hashMap",
  "two-pointers": "topic_twoPointers",
  "binary-search": "topic_binarySearch",
  grid: "topic_grid",
  dp: "topic_dp",
};

type TopicFilter = "all" | InterviewTopic;

const RING_RADIUS = 34;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

function titleOf(problem: { title_en: string; title_bn: string | null }, locale: string): string {
  return locale === "bn" && problem.title_bn ? problem.title_bn : problem.title_en;
}

/**
 * The head's right-hand card: the reader's progress on the list and the one
 * next step (the first problem in list order they have not solved), or the
 * reason to sign in. Holds its place while the session or the list settles,
 * so a member never sees the sign-in card flash first.
 */
function InterviewProgress({
  locale,
  session,
  data,
}: {
  locale: string;
  session: "settling" | "anonymous" | "member";
  data: InterviewPrepList | undefined;
}) {
  const t = useTranslations("judge.interview");
  const n = (value: number) => formatNumber(value, locale);
  const signInHref = useSignInHref(locale);

  if (session === "anonymous") {
    return (
      <div className="rounded-xl border border-white/10 bg-neutral-900 p-5 sm:p-6">
        <p className="text-base font-bold">{t("guestTitle")}</p>
        <p className="mt-2 text-sm leading-relaxed text-neutral-400">{t("guestBody")}</p>
        <Button asChild variant="inverse" className="mt-4 h-10">
          <Link href={signInHref}>{t("signIn")}</Link>
        </Button>
      </div>
    );
  }

  if (!data?.viewer) {
    return (
      <div data-testid="interview-progress-loading" className="flex items-center gap-5 rounded-xl border border-white/10 bg-neutral-900 p-5 sm:p-6">
        <Skeleton className="h-[84px] w-[84px] shrink-0 rounded-full bg-white/10" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-2 w-full bg-white/10" />
          <Skeleton className="h-2 w-full bg-white/10" />
          <Skeleton className="h-2 w-2/3 bg-white/10" />
        </div>
      </div>
    );
  }

  const { viewer } = data;
  const total = data.total;
  const solved = Math.min(viewer.solved, total);
  const next = data.problems.find((problem) => problem.solvedState !== "solved");
  const seeded = data.topics.filter((topic) => topic.total > 0);

  return (
    <div data-testid="interview-progress" className="rounded-xl border border-white/10 bg-neutral-900 p-5 sm:p-6">
      <div className="flex items-center gap-[18px]">
        <div
          role="img"
          aria-label={t("ringLabel", { solved: n(solved), total: n(total) })}
          className="relative h-[84px] w-[84px] shrink-0"
        >
          <svg viewBox="0 0 84 84" className="h-[84px] w-[84px] -rotate-90" aria-hidden>
            <circle cx="42" cy="42" r={RING_RADIUS} fill="none" strokeWidth="8" className="stroke-neutral-800" />
            {solved > 0 && total > 0 ? (
              <circle
                cx="42"
                cy="42"
                r={RING_RADIUS}
                fill="none"
                strokeWidth="8"
                strokeLinecap="round"
                className="stroke-primary transition-[stroke-dasharray] duration-200 ease-out"
                strokeDasharray={`${(RING_LENGTH * solved) / total} ${RING_LENGTH}`}
              />
            ) : null}
          </svg>
          <span className="absolute inset-0 flex flex-col items-center justify-center leading-tight" aria-hidden>
            <b className="font-mono text-xl font-bold">
              {n(solved)}/{n(total)}
            </b>
            <span className="text-[11px] text-neutral-400">{t("ringSolved")}</span>
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white">
            {viewer.solvedRecently > 0
              ? t("thisWeek", { n: viewer.solvedRecently, count: n(viewer.solvedRecently) })
              : t("thisWeekNone")}
          </p>
          {seeded.length > 0 ? (
            <ul className="mt-3 grid grid-cols-7 gap-1.5" aria-label={t("topicsProgressLabel")}>
              {seeded.map((topic) => {
                const done = Math.min(topic.solved ?? 0, topic.total);
                return (
                  <li key={topic.key} title={`${t(TOPIC_KEY[topic.key])}: ${n(done)}/${n(topic.total)}`}>
                    <span className="block h-1.5 overflow-hidden rounded-full bg-neutral-800" aria-hidden>
                      <i
                        className="block h-full rounded-full bg-primary transition-[width] duration-200 ease-out"
                        style={{ width: `${(100 * done) / topic.total}%` }}
                      />
                    </span>
                    <span className="sr-only">
                      {t("topicProgress", { topic: t(TOPIC_KEY[topic.key]), solved: n(done), total: n(topic.total) })}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      </div>
      {next ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
          <p className="min-w-0 text-sm text-neutral-400">
            {t("nextUp")} <span className="font-medium text-white">{titleOf(next, locale)}</span>
          </p>
          <Button asChild size="md" className="h-10">
            <Link href={`/${locale}/judge/${next.slug}`}>
              {solved === 0 ? t("startFirst") : t("solveNext")}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </Button>
        </div>
      ) : total > 0 ? (
        <p className="mt-4 border-t border-white/10 pt-4 text-sm leading-relaxed text-neutral-400">{t("allDone")}</p>
      ) : null}
    </div>
  );
}

function ProblemRow({
  problem,
  locale,
  showStatus,
  countLabels,
}: {
  problem: InterviewPrepProblem;
  locale: string;
  showStatus: boolean;
  countLabels: SolveCountLabels;
}) {
  const tCatalog = useTranslations("judge.catalog");
  const title = titleOf(problem, locale);
  const otherTitle = problem.title_bn ? (locale === "bn" ? problem.title_en : problem.title_bn) : null;
  const mark =
    problem.solvedState === "solved"
      ? tCatalog("mark_solved")
      : problem.solvedState === "attempted"
        ? tCatalog("mark_attempted")
        : tCatalog("mark_untried");

  return (
    <li>
      <Link
        href={`/${locale}/judge/${problem.slug}`}
        className={cn(
          "group grid items-center gap-x-4 gap-y-1 px-4 py-3.5 transition-colors duration-200 hover:bg-primary/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:px-5",
          showStatus
            ? "grid-cols-[22px_minmax(0,1fr)_auto] sm:grid-cols-[22px_minmax(0,1fr)_auto_minmax(96px,auto)_16px]"
            : "grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_auto_minmax(96px,auto)_16px]",
        )}
      >
        {showStatus ? <SolvedMark state={problem.solvedState} label={mark} /> : null}
        <span className="min-w-0">
          <span className="block break-words text-[15px] font-bold tracking-tight text-neutral-900">{title}</span>
          {otherTitle ? <span className="block text-[13px] text-neutral-400">{otherTitle}</span> : null}
        </span>
        <DifficultyTag difficulty={problem.difficulty} label={tCatalog(`difficulty_${problem.difficulty}`)} />
        <SolveCount
          solvers={problem.solvers}
          attempters={problem.attempters}
          submissions={problem.submissions}
          locale={locale}
          labels={countLabels}
          className="hidden justify-self-end sm:flex"
        />
        <ArrowRight
          className="hidden h-4 w-4 justify-self-end text-primary-ink opacity-0 transition-[opacity,transform] duration-200 -translate-x-1 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100 motion-reduce:transition-none sm:block"
          aria-hidden
        />
      </Link>
    </li>
  );
}

function ListSkeleton() {
  return (
    <div data-testid="interview-loading" className="space-y-6">
      {[0, 1].map((group) => (
        <div key={group} className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
          <div className="border-b border-neutral-100 px-5 py-4">
            <Skeleton className="h-5 w-40" />
          </div>
          {[0, 1, 2].map((row) => (
            <div key={row} className="flex items-center gap-4 border-b border-neutral-100 px-5 py-4 last:border-b-0">
              <Skeleton className="h-[22px] w-[22px] rounded-full" />
              <Skeleton className="h-4 w-56" />
              <Skeleton className="ml-auto h-6 w-16 rounded-md" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * `/judge/interview`, Interview Prep (`EX-FNC07`, board H, Wave 36 decision
 * D2): the free list of Progsity's own function problems, by topic. A dark
 * head with what the list is and the reader's progress (or the reason to sign
 * in), then the topics as filter chips and one card per topic with its rows.
 * `?topic=` opens on a topic and the chips keep it in the address, so a topic
 * can be shared. Everything reads signed out; the status marks, the progress
 * and "Hide solved" exist only for a reader they can describe.
 */
export function InterviewPrepClient({ locale }: { locale: string }) {
  const t = useTranslations("judge.interview");
  const tCount = useTranslations("judge.solveCount");
  const countLabels = useMemo(() => solveCountLabels(tCount, locale), [tCount, locale]);
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const n = (value: number) => formatNumber(value, locale);

  const [topic, setTopic] = useState<TopicFilter>("all");
  const [hideSolved, setHideSolved] = useState(false);

  // Read once on mount, as the library reads `?tag=`: a statically rendered
  // page does not wait on `useSearchParams` for one value.
  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get("topic");
    if (isInterviewTopic(param)) setTopic(param);
  }, []);

  function chooseTopic(next: TopicFilter) {
    setTopic(next);
    const url = new URL(window.location.href);
    if (next === "all") url.searchParams.delete("topic");
    else url.searchParams.set("topic", next);
    window.history.replaceState(window.history.state, "", url.toString());
  }

  const viewerKey = authLoading ? "loading" : isAuthenticated ? "member" : "anon";
  // Held until the session settles, so the anonymous answer is never shown to a member.
  const query = useInterviewPrep(viewerKey, !authLoading);
  const data = query.data;
  const signedIn = isAuthenticated && !authLoading;
  const showStatus = signedIn && data?.viewer !== null && data?.viewer !== undefined;
  const effectiveHide = showStatus && hideSolved;

  const groups = useMemo(() => {
    if (!data) return [];
    return INTERVIEW_TOPICS.filter((key) => topic === "all" || key === topic)
      .map((key) => {
        const summary = data.topics.find((item) => item.key === key) ?? { key, total: 0, solved: null };
        const rows = data.problems.filter(
          (problem) => problem.topic === key && !(effectiveHide && problem.solvedState === "solved"),
        );
        return { key, summary, rows };
      })
      .filter((group) => group.summary.total > 0);
  }, [data, topic, effectiveHide]);

  const shown = groups.reduce((total, group) => total + group.rows.length, 0);

  return (
    <>
      <JudgeDarkHead
        eyebrow={t("eyebrow")}
        eyebrowIcon={<BriefcaseBusiness className="h-4 w-4" />}
        title={t("title")}
        titleId="interview-prep-title"
        description={t("description")}
        aside={<InterviewProgress locale={locale} session={authLoading ? "settling" : signedIn ? "member" : "anonymous"} data={data} />}
      >
        <ul className="mt-6 flex flex-wrap gap-2 text-[13px] text-neutral-200">
          <li className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 font-semibold text-accent-peach">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            {t("free")}
          </li>
          <li className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1">
            <Code2 className="h-3.5 w-3.5 text-primary" aria-hidden />
            {t("oneFunction")}
          </li>
          <li className="inline-flex items-center rounded-full border border-white/15 px-3 py-1 font-mono text-[12px]">
            C · C++ · Java · Python · JavaScript
          </li>
        </ul>
      </JudgeDarkHead>

      <Container className="pb-16 pt-7 sm:pb-[72px]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div role="group" aria-label={t("topicsLabel")} className="flex max-w-full flex-wrap gap-2">
            <Button
              variant="filter"
              size="chipLg"
              aria-pressed={topic === "all"}
              onClick={() => chooseTopic("all")}
            >
              {t("topicAll")}
              {data ? <>{" "}<span className="font-mono text-xs opacity-75">{n(data.total)}</span></> : null}
            </Button>
            {INTERVIEW_TOPICS.map((key) => {
              const total = data?.topics.find((item) => item.key === key)?.total ?? 0;
              return (
                <Button
                  key={key}
                  variant="filter"
                  size="chipLg"
                  aria-pressed={topic === key}
                  onClick={() => chooseTopic(key)}
                >
                  {t(TOPIC_KEY[key])}
                  {data ? <>{" "}<span className="font-mono text-xs opacity-75">{n(total)}</span></> : null}
                </Button>
              );
            })}
          </div>
          {showStatus ? (
            <Button
              variant="filter"
              size="chipLg"
              aria-pressed={hideSolved}
              onClick={() => setHideSolved((value) => !value)}
            >
              {t("hideSolved")}
            </Button>
          ) : null}
        </div>

        <div className="mt-6" aria-live="polite">
          {query.isPending || authLoading ? (
            <ListSkeleton />
          ) : query.isError || !data ? (
            <div className="rounded-xl border border-neutral-200 bg-white px-6 py-10 text-center">
              <p className="text-base font-bold text-neutral-900">{t("errorTitle")}</p>
              <p className="mt-1.5 text-sm text-neutral-500">{t("errorBody")}</p>
              <Button variant="outline" className="mt-4" onClick={() => void query.refetch()}>
                {t("retry")}
              </Button>
            </div>
          ) : data.total === 0 ? (
            <div className="rounded-xl border border-dashed border-neutral-300 bg-white px-6 py-12 text-center">
              <p className="text-base font-bold text-neutral-900">{t("emptyTitle")}</p>
              <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-neutral-500">{t("emptyBody")}</p>
              <Button asChild variant="outline" className="mt-4">
                <Link href={`/${locale}/judge/problems`}>{t("emptyLink")}</Link>
              </Button>
            </div>
          ) : groups.length === 0 ? (
            <div className="rounded-xl border border-dashed border-neutral-300 bg-white px-6 py-12 text-center">
              <p className="text-base font-bold text-neutral-900">{t("topicEmptyTitle")}</p>
              <p className="mt-1.5 text-sm text-neutral-500">{t("topicEmptyBody")}</p>
              <Button variant="outline" className="mt-4" onClick={() => chooseTopic("all")}>
                {t("topicAll")}
              </Button>
            </div>
          ) : (
            <>
              <p className="sr-only">{t("shownCount", { n: shown, count: n(shown) })}</p>
              <div data-testid="interview-topics" className="grid gap-6 xl:grid-cols-2">
                {groups.map((group) => {
                  const solved = group.summary.solved;
                  return (
                    <section
                      key={group.key}
                      aria-labelledby={`interview-topic-${group.key}`}
                      className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
                    >
                      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3.5 sm:px-5">
                        <h2 id={`interview-topic-${group.key}`} className="text-base font-bold text-neutral-900">
                          {t(TOPIC_KEY[group.key])}{" "}
                          <span className="ml-1.5 font-mono text-xs font-medium text-neutral-400">{n(group.summary.total)}</span>
                        </h2>
                        {solved !== null ? (
                          <span className="flex items-center gap-2 text-[13px] text-neutral-500">
                            <span className="h-1.5 w-20 overflow-hidden rounded-full bg-neutral-100" aria-hidden>
                              <i
                                className="block h-full rounded-full bg-primary transition-[width] duration-200 ease-out"
                                style={{ width: `${(100 * Math.min(solved, group.summary.total)) / group.summary.total}%` }}
                              />
                            </span>
                            {t("topicSolved", { solved: n(solved), total: n(group.summary.total) })}
                          </span>
                        ) : null}
                      </header>
                      {group.rows.length > 0 ? (
                        <ul className="divide-y divide-neutral-100">
                          {group.rows.map((problem) => (
                            <ProblemRow
                              key={problem.id}
                              problem={problem}
                              locale={locale}
                              showStatus={showStatus}
                              countLabels={countLabels}
                            />
                          ))}
                        </ul>
                      ) : (
                        <p className="px-5 py-6 text-sm text-neutral-500">{t("topicAllSolved")}</p>
                      )}
                    </section>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </Container>
    </>
  );
}
